import React, { useState, useEffect, useCallback } from 'react';
import SharedHeader from '../../components/shared/SharedHeader';
import ProtectedPage from '../../components/shared/ProtectedPage';
import { usePermissions } from '../../utils/usePermissions';
import MonthsView from './components/MonthsView';
import DayView from './components/DayView';
import CreateEventModal from './components/CreateEventModal';
import EventDetailsModal from './components/EventDetailsModal';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import { toast } from '../../components/shared/toast';
import { apiClient } from '../../api/apiClient';
import { GET_CALENDAR_EVENTS_URL, DELETE_CALENDAR_EVENT_URL } from '../../api/calendar';
import './Calendar.css';

function Calendar() {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateCalendar = canCreate('calendar');
  const canEditCalendar = canEdit('calendar');
  const canDeleteCalendar = canDelete('calendar');

  const [activeTab, setActiveTab] = useState('Months'); // Default selected tab: Months
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(''); // Initially no date selected
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [selectedEventForView, setSelectedEventForView] = useState(null);
  const [eventToDelete, setEventToDelete] = useState(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch events from backend
  const fetchEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await apiClient(GET_CALENDAR_EVENTS_URL);
      setEvents(res?.events || []);
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      toast.error('Failed to load calendar events.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleAddNew = () => {
    setEventToEdit(null);
    setShowCreateModal(true);
  };

  const handleSaveSuccess = (savedEvent) => {
    if (savedEvent && savedEvent.event_date) {
      setSelectedDate(savedEvent.event_date);
    }
    fetchEvents();
  };

  // Handle Year Switcher
  const handleYearChange = (delta) => {
    setCurrentYear((prev) => prev + delta);
  };

  // Handle Month Switcher in Day View
  const handleMonthChange = (delta) => {
    let newM = currentMonth + delta;
    let newY = currentYear;
    if (newM < 0) {
      newM = 11;
      newY -= 1;
    } else if (newM > 11) {
      newM = 0;
      newY += 1;
    }
    setCurrentMonth(newM);
    setCurrentYear(newY);
  };

  // Handle Month Card Click in Months View
  const handleSelectMonthFromGrid = (monthIdx) => {
    setCurrentMonth(monthIdx);
    setActiveTab('Day');
    // Pre-select 1st of that month
    const formattedMonth = String(monthIdx + 1).padStart(2, '0');
    setSelectedDate(`${currentYear}-${formattedMonth}-01`);
  };

  // Delete event confirmation
  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    if (eventToDelete.type && eventToDelete.type !== 'event') {
      toast.warning('Notes and Situations cannot be deleted from the Calendar tab.');
      setEventToDelete(null);
      return;
    }
    try {
      await apiClient(DELETE_CALENDAR_EVENT_URL(eventToDelete.original_id || eventToDelete.id), {
        method: 'DELETE'
      });
      toast.success('Event deleted successfully.');
      setEventToDelete(null);
      fetchEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
      toast.error('Failed to delete event.');
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!selectedDate) {
      toast.warning('Please select a date from the calendar to export events.');
      return;
    }

    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      const dateEvents = events.filter(e => e.event_date === selectedDate);
      if (dateEvents.length === 0) {
        toast.warning(`No events scheduled on ${selectedDate} to export.`);
        return;
      }

      const headers = ['Event Name', 'Date', 'Time', 'Description', 'Created At'];
      const rows = dateEvents.map(e => [
        e.event_name || '—',
        e.event_date || '—',
        e.event_time || '—',
        e.description || '—',
        e.created_at ? String(e.created_at).split('T')[0] : '—'
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `calendar_events_${selectedDate}_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Calendar events exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export calendar events.');
    } finally {
      setIsExporting(false);
    }
  };

  // Filter events by search query if any
  const filteredEvents = events.filter((e) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (e.event_name && e.event_name.toLowerCase().includes(q)) ||
      (e.description && e.description.toLowerCase().includes(q))
    );
  });

  return (
    <ProtectedPage module="calendar">
      <div className="calendar-page-wrapper">
      {/* Shared Header Controls */}
      <SharedHeader
        title="Calendar"
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search calendar events..."
        resultCount={filteredEvents.length}
        resultLabel="Events"
        onExportClick={handleExport}
        exportDisabled={!selectedDate}
        onAddNewClick={canCreateCalendar ? handleAddNew : null}
        addNewText="Add New"
        addNewDisabled={false}
        canAddNew={canCreateCalendar}
      />

      {/* Top Navigation Sub-Tabs: Day / Months */}
      <div className="calendar-tabs-row" style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
        <button
          type="button"
          className={`eaccounts-tab-btn ${activeTab === 'Day' ? 'active' : ''}`}
          onClick={() => setActiveTab('Day')}
          style={{
            padding: '8px 24px',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '14px',
            border: '1px solid #cbd5e1',
            backgroundColor: activeTab === 'Day' ? 'var(--primary-color, #0b66e3)' : '#ffffff',
            color: activeTab === 'Day' ? '#ffffff' : '#475569',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Day
        </button>

        <button
          type="button"
          className={`eaccounts-tab-btn ${activeTab === 'Months' ? 'active' : ''}`}
          onClick={() => setActiveTab('Months')}
          style={{
            padding: '8px 24px',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '14px',
            border: '1px solid #cbd5e1',
            backgroundColor: activeTab === 'Months' ? 'var(--primary-color, #0b66e3)' : '#ffffff',
            color: activeTab === 'Months' ? '#ffffff' : '#475569',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Months
        </button>
      </div>

      {/* Render Active View */}
      {activeTab === 'Months' ? (
        <MonthsView
          year={currentYear}
          events={filteredEvents}
          onSelectMonth={handleSelectMonthFromGrid}
          onYearChange={handleYearChange}
        />
      ) : (
        <DayView
          year={currentYear}
          month={currentMonth}
          selectedDate={selectedDate}
          onSelectDate={(d) => setSelectedDate(d)}
          onMonthChange={handleMonthChange}
          onYearChange={handleYearChange}
          events={filteredEvents}
          onEventClick={(evt) => setSelectedEventForView(evt)}
          onDeleteEventClick={canDeleteCalendar ? (evt) => setEventToDelete(evt) : null}
          onAddNewClick={canCreateCalendar ? handleAddNew : null}
        />
      )}

      {/* Create / Edit Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEventToEdit(null);
          }}
          onSuccess={handleSaveSuccess}
          initialDate={selectedDate}
          eventToEdit={eventToEdit}
        />
      )}

      {/* Event Details View Modal */}
      {selectedEventForView && (
        <EventDetailsModal
          isOpen={!!selectedEventForView}
          onClose={() => setSelectedEventForView(null)}
          event={selectedEventForView}
          onDeleteClick={canDeleteCalendar ? (evt) => setEventToDelete(evt) : null}
          onEditClick={canEditCalendar ? (evt) => {
            setEventToEdit(evt);
            setShowCreateModal(true);
          } : null}
        />
      )}

      {/* Global Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!eventToDelete}
        type="danger"
        title="Delete Event"
        message="Are you sure you want to delete this event?"
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setEventToDelete(null)}
      />

      {/* Global Loading Overlay */}
      <LoadingOverlay isLoading={isExporting || isLoading} message={isExporting ? "Exporting calendar data..." : "Loading calendar..."} />
      </div>
    </ProtectedPage>
  );
}

export default Calendar;
